import React, { useEffect, useState, useRef } from 'react';
import isEmpty from 'lodash.isempty';
import { notification, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { TabLeftPanel, Loader } from 'react-inv-forms';
import { useTranslation } from 'react-i18next';

import TableCellTypes from '../../../helpers/tableCellTypes';
import StringBuilder from '../../../helpers/StringBuilder';

interface MySubjectsDetailsListProps {
  /** dane wejściowe komponentu */
  data: SingleObjectDataStructure;
  /** stan ładowania danych */
  loading: boolean;
  /** tablica kolumn (pozycji) */
  columns: SingleColumn[];
}

/**
 *  Komponent odpowiedzialny za renderowanie listy szczegółów przedmiotu (wypełnienie lewej ramki strony pojedynczego przedmiotu)
 *  oraz sprawdzeniu informacji o przydziałach i wyświetlaniu odpowiednich notyfikacji z nimi związanych
 *
 */
export const MySubjectsDetailsList = ({ data, loading, columns }: MySubjectsDetailsListProps): JSX.Element => {
  const { t } = useTranslation();
  const [processedData, setProcessedData] = useState<SingleObjectDataStructure>();
  const notificationShownRef = useRef<boolean>();

  useEffect(() => {
    if (isEmpty(data)) {
      return;
    }

    const content = TableCellTypes.generateTableContent([data], columns)[0];
    content.typPrzydzialu = data.typPrzydzialu;

    if (data.wagiFormyZajecMapa) {
      content.sumaWag = Object.keys(data.wagiFormyZajecMapa).reduce(
        (total, key) =>
          !isNaN(Number(data.wagiFormyZajecMapa[key])) ? parseFloat(data.wagiFormyZajecMapa[key]) + total : 0 + total,
        0,
      );
    }

    setProcessedData(content);
  }, [data, columns, t]);

  useEffect(() => {
    if (!processedData || notificationShownRef.current) {
      return;
    }

    notificationShownRef.current = true;

    if (processedData.typPrzydzialu === 'brak_przydzialu_sid') {
      openWarnNotification('PRZYDZIAL_BRAK_PRZYDZIALU_SID_TITLE', 'PRZYDZIAL_BRAK_PRZYDZIALU_SID_DESCRIPTION');
    }

    if (!processedData.sumaWag || processedData.sumaWag !== 100) {
      openWarnNotification('NO_GRADES_IMPORTANCE_TITLE', 'NO_GRADES_IMPORTANCE');
    }
  }, [processedData]);

  const openWarnNotification = (messageKey: string, descriptionKey: string) =>
    notification.warn({
      message: t(messageKey),
      description: t(descriptionKey),
      top: 100,
    });

  return (
    <TabLeftPanel>
      {loading ? (
        <Loader
          style={{
            position: 'absolute',
            left: '40%',
            top: '10%',
            fontSize: '32px',
          }}
        />
      ) : processedData ? (
        <>
          <section>
            <h4>{t('PRZYDZIAL_PRZEDMIOTNAZWA')}</h4>
            <strong>{processedData.przedmiotNazwa}</strong>
          </section>
          <section>
            <h4>{t('PRZYDZIAL_PRZEDMIOTKOD')}</h4>
            <strong>{processedData.przedmiotKod}</strong>
          </section>
          <section>
            <h4>{t('PRZYDZIAL_SEMESTRAKADEMICKI')}</h4>
            <strong>{StringBuilder.buildReadableSemester(processedData.rokAkademicki, processedData.semestrAkademicki)}</strong>
          </section>
          <section>
            <h4>{t('PRZYDZIAL_WYDZIALZLECAJACYNAZWA')}</h4>
            <strong>{processedData.wydzialZlecajacyNazwa}</strong>
          </section>
          <section>
            <h4>{t('CZY_PROTOKOL_AKTUALNY')}</h4>
            <strong>{processedData.czyProtokolAktualny}</strong>
          </section>
          <section>
            <h4>{t('PRZYDZIAL_PROWADZACYNAZWALISTA')}</h4>
            <strong>{processedData.prowadzacyNazwaLista}</strong>
          </section>
          <section>
            <h4>{t('KIEROWNICY_KARTY_LISTA')}</h4>
            <strong>{processedData.kierownicyKartyLista}</strong>
          </section>
          <section>
            <h4>{t('PRZYDZIAL_WAGIFORMYZAJEC')}</h4>
            <strong style={{ marginBottom: 0 }}>
              {processedData.wagiFormyZajec ? (
                processedData.sumaWag === 100 ? (
                  processedData.wagiFormyZajec
                ) : (
                  <Tooltip title={t('NO_GRADES_IMPORTANCE')}>
                    <span className='wagi-warning'>
                      <WarningOutlined style={{ marginRight: '4px' }} />
                      {t('NO_GRADES_IMPORTANCE_TITLE')}
                    </span>
                  </Tooltip>
                )
              ) : (
                <span>{t('NO_PARTIAL_MARKS')}</span>
              )}
            </strong>
          </section>
        </>
      ) : (
        <span className='block-centered'>{t('NO_DATA')}</span>
      )}
    </TabLeftPanel>
  );
};
